import {
  RefreshCcw,
  Activity,
  MousePointer2,
  Bell,
  Check,
  X,
  Eye,
  ArrowLeft,
  Menu,
} from "lucide-react";

/**
 * "Drift story" — a seamless 10-second loop that tells the full Mindrail story:
 *   1. NORMAL WORK     (0.0 – 1.5s)  — Research doc tab active, cursor calm
 *   2. DRIFT STARTS    (1.5 – 3.0s)  — tabs flick, cursor wanders
 *   3. SENSING         (3.0 – 4.5s)  — signal chips pulse, waveform animates
 *   4. DRIFT DETECTED  (4.5 – 6.0s)  — soft amber wash + amber dot on timeline
 *   5. GENTLE NUDGE    (6.0 – 8.0s)  — Mindrail card slides in over workspace
 *   6. BACK IN FOCUS   (8.0 – 9.5s)  — tab settles to Research doc, green pill
 *   ... seamless loop back to NORMAL WORK
 *
 * Pure SVG/HTML + CSS keyframes (scoped under .mr-drift-story in App.css).
 */
export default function DriftRadarAnimation() {
  return (
    <div className="absolute inset-0 mr-drift-story overflow-hidden">
      {/* Soft background waveform + ambient particles */}
      <svg
        viewBox="0 0 400 500"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        {/* upper waveforms (sensing) */}
        <g
          className="mr-wave-top"
          stroke="#2563EB"
          strokeWidth="1"
          fill="none"
          strokeLinecap="round"
        >
          <path d="M 20 90 Q 60 70 100 90 T 180 88 T 260 80 T 340 90 T 390 86" />
          <path
            d="M 30 110 Q 80 95 130 108 T 230 100 T 330 112 T 390 105"
            opacity="0.6"
          />
        </g>
        {/* lower waveform (drift) */}
        <g
          className="mr-wave-bot"
          stroke="#F59E0B"
          strokeWidth="0.9"
          fill="none"
          strokeLinecap="round"
        >
          <path d="M 30 420 L 55 405 L 80 425 L 105 410 L 130 428 L 155 412 L 180 426 L 205 414 L 230 428 L 255 416 L 280 426 L 305 418 L 330 428 L 355 420 L 380 426" />
        </g>
        {/* ambient particles */}
        <circle className="mr-amb mr-amb-1" cx="48" cy="240" r="2" />
        <circle className="mr-amb mr-amb-2" cx="360" cy="220" r="2.2" />
        <circle className="mr-amb mr-amb-3" cx="380" cy="340" r="1.8" />
        <circle className="mr-amb mr-amb-4" cx="34" cy="320" r="2" />
      </svg>

      {/* ===== SIGNAL CHIP: Tab switching ===== */}
      <div
        className="mr-chip mr-chip-1 absolute"
        style={{ left: "5%", top: "5%" }}
        aria-hidden
      >
        <div className="bg-white border border-[#E2E8F0] rounded-full shadow-[0_8px_20px_rgba(15,23,42,0.06)] px-2.5 py-1.5 flex items-center gap-1.5">
          <span className="w-5 h-5 rounded-full bg-[#DBEAFE] flex items-center justify-center">
            <RefreshCcw
              className="w-2.5 h-2.5 text-[#2563EB]"
              strokeWidth={2.2}
            />
          </span>
          <span className="font-display text-[10px] font-semibold text-[#0F172A] whitespace-nowrap">
            Tab switching
          </span>
          <span className="flex gap-0.5 ml-0.5">
            <span className="mr-cdot mr-cdot-1 w-1.5 h-1.5 rounded-full" />
            <span className="mr-cdot mr-cdot-2 w-1.5 h-1.5 rounded-full" />
            <span className="mr-cdot mr-cdot-3 w-1.5 h-1.5 rounded-full" />
          </span>
        </div>
      </div>

      {/* ===== SIGNAL CHIP: Mouse jitter ===== */}
      <div
        className="mr-chip mr-chip-2 absolute"
        style={{ right: "4%", top: "3%" }}
        aria-hidden
      >
        <div className="bg-white border border-[#E2E8F0] rounded-full shadow-[0_8px_20px_rgba(15,23,42,0.06)] px-2.5 py-1.5 flex items-center gap-1.5">
          <span className="w-5 h-5 rounded-full bg-[#FEF3C7] flex items-center justify-center">
            <Activity
              className="w-2.5 h-2.5 text-[#F59E0B]"
              strokeWidth={2.2}
            />
          </span>
          <span className="font-display text-[10px] font-semibold text-[#0F172A] whitespace-nowrap">
            Mouse jitter
          </span>
          <span className="flex gap-0.5 ml-0.5">
            <span className="mr-cdot mr-cdot-1 w-1.5 h-1.5 rounded-full" />
            <span className="mr-cdot mr-cdot-2 w-1.5 h-1.5 rounded-full" />
            <span className="mr-cdot mr-cdot-3 w-1.5 h-1.5 rounded-full" />
          </span>
        </div>
      </div>

      {/* ===== SIGNAL CHIP: Cursor leaving ===== */}
      <div
        className="mr-chip mr-chip-3 absolute"
        style={{ left: "2%", top: "44%" }}
        aria-hidden
      >
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_8px_20px_rgba(15,23,42,0.06)] px-3 py-2 flex flex-col items-start gap-1">
          <div className="flex items-center gap-1.5">
            <MousePointer2
              className="w-3 h-3 text-[#475569]"
              strokeWidth={2}
            />
            <span className="font-display text-[10px] font-semibold text-[#0F172A] whitespace-nowrap">
              Cursor leaving
            </span>
          </div>
          <span className="flex gap-0.5">
            <span className="mr-cdot mr-cdot-1 w-1.5 h-1.5 rounded-full" />
            <span className="mr-cdot mr-cdot-2 w-1.5 h-1.5 rounded-full" />
          </span>
        </div>
      </div>

      {/* ===== BROWSER WINDOW ===== */}
      <div
        className="absolute"
        style={{ left: "16%", top: "14%", right: "5%", bottom: "20%" }}
      >
        <div className="relative bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_24px_60px_rgba(15,23,42,0.10)] overflow-hidden h-full flex flex-col">
          {/* chrome bar */}
          <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-2.5 py-1.5 flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-[#F9735B]" />
            <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
            <span className="w-2 h-2 rounded-full bg-[#22C55E]" />
            <div className="ml-1 flex-1 relative flex items-stretch h-5 text-[8px]">
              {/* tab active indicator (slides) */}
              <span
                className="mr-tab-active absolute top-0 bottom-0 rounded-t-md bg-white border border-[#E2E8F0] border-b-0"
                style={{ width: "20%" }}
              />
              {/* 5 tabs */}
              {[
                { label: "Inbox (14)", kind: "gmail" },
                { label: "Research doc", kind: "doc" },
                { label: "Notes – Ideas", kind: "note" },
                { label: "YouTube", kind: "yt" },
                { label: "Reddit", kind: "reddit" },
              ].map((t, i) => (
                <div
                  key={i}
                  className="relative flex-1 px-1 flex items-center gap-1 text-[#475569] min-w-0 z-10"
                >
                  <TabIcon kind={t.kind} />
                  <span className="truncate font-display font-medium">
                    {t.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* nav bar */}
          <div className="px-2.5 py-1 border-b border-[#E2E8F0] flex items-center gap-2 shrink-0">
            <ArrowLeft className="w-2.5 h-2.5 text-[#94A3B8]" strokeWidth={2} />
            <Menu className="w-2.5 h-2.5 text-[#94A3B8]" strokeWidth={2} />
          </div>

          {/* content */}
          <div className="flex-1 p-3 flex gap-2.5 relative min-h-0">
            <div className="flex-1 min-w-0">
              <p className="font-display text-[11px] font-bold text-[#0F172A]">
                Research Notes
              </p>
              <div className="mt-2 space-y-1.5">
                {[100, 75, 92, 60, 85, 70, 95, 65, 80, 72, 88, 58].map(
                  (w, i) => (
                    <span
                      key={i}
                      className="block h-1 rounded-full bg-[#E2E8F0]"
                      style={{ width: `${w}%` }}
                    />
                  )
                )}
              </div>
            </div>
            {/* sidebar */}
            <div className="w-[22%] space-y-1.5 shrink-0">
              <span className="block h-7 rounded-md bg-[#DCFCE7]" />
              <span className="block h-1 rounded-full bg-[#E2E8F0] w-3/4" />
              <span className="block h-7 rounded-md bg-[#FEF3C7]" />
              <span className="block h-1 rounded-full bg-[#E2E8F0]" />
              <span className="block h-1 rounded-full bg-[#E2E8F0] w-5/6" />
              <span className="block h-7 rounded-md bg-[#FFE4DE]" />
            </div>

            {/* animated cursor */}
            <span className="mr-cursor absolute pointer-events-none">
              <CursorIcon />
            </span>

            {/* orange drift wash overlay */}
            <div
              className="mr-orange-wash absolute inset-0 pointer-events-none"
              aria-hidden
            />
          </div>

          {/* notification card overlay */}
          <div
            className="mr-notif absolute left-[8%] right-[26%] top-[36%]"
            aria-hidden
          >
            <div className="bg-white rounded-xl shadow-[0_18px_40px_rgba(15,23,42,0.18)] border-l-[3px] border-[#2563EB] border-y border-r border-[#E2E8F0] px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="font-label text-[8px] font-bold tracking-[0.22em] text-[#2563EB]">
                  MINDRAIL
                </span>
                <span className="w-4 h-4 rounded border border-[#E2E8F0] flex items-center justify-center">
                  <X className="w-2 h-2 text-[#94A3B8]" strokeWidth={2.5} />
                </span>
              </div>
              <p className="mt-1.5 text-[9px] text-[#475569]">
                Attention looks scattered.
              </p>
              <p className="font-display text-[10px] font-bold text-[#0F172A] mt-0.5 leading-tight">
                Pick one tab and stay there.
              </p>
              <div className="mt-2 flex gap-1.5">
                <span className="text-[8px] font-medium px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[#0F172A]">
                  Dismiss
                </span>
                <span className="text-[8px] font-medium px-1.5 py-0.5 rounded border border-[#2563EB] text-[#2563EB]">
                  Snooze 10 min
                </span>
              </div>
            </div>
          </div>

          {/* "back in focus" pill */}
          <div
            className="mr-back-pill absolute right-3 bottom-3"
            aria-hidden
          >
            <div className="bg-white border border-[#DCFCE7] rounded-full shadow-[0_8px_20px_rgba(34,197,94,0.18)] px-2.5 py-1 flex items-center gap-1">
              <Check
                className="w-2.5 h-2.5 text-[#22C55E]"
                strokeWidth={2.8}
              />
              <span className="font-display text-[9px] font-bold text-[#22C55E] whitespace-nowrap">
                Back in focus
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== BOTTOM TIMELINE ===== */}
      <div
        className="mr-timeline absolute left-3 right-3 bottom-3"
        aria-hidden
      >
        <div className="relative px-1">
          {/* base track */}
          <div className="absolute left-3 right-3 top-[7px] h-px bg-[#E2E8F0]" />
          {/* progress fill */}
          <div
            className="mr-tl-fill absolute left-3 top-[7px] h-[2px] rounded-full"
            style={{
              background:
                "linear-gradient(90deg, #2563EB 0%, #2563EB 40%, #F59E0B 55%, #2563EB 75%, #22C55E 100%)",
            }}
          />
          <div className="relative flex justify-between items-start">
            {[
              { l: "Monitoring", icon: Eye, c: "#2563EB" },
              { l: "Sensing", icon: Activity, c: "#2563EB" },
              { l: "Drift detected", icon: Activity, c: "#F59E0B" },
              { l: "Gentle nudge", icon: Bell, c: "#2563EB" },
              { l: "Back in focus", icon: Check, c: "#22C55E" },
            ].map((s, i) => (
              <div
                key={i}
                className={`mr-tl-node mr-tl-node-${i} flex flex-col items-center gap-1 w-1/5`}
              >
                <span
                  className="mr-tl-dot w-3.5 h-3.5 rounded-full bg-white border-2 border-[#E2E8F0] flex items-center justify-center"
                  style={{ "--c": s.c }}
                >
                  <s.icon
                    className="w-[7px] h-[7px]"
                    strokeWidth={3}
                    style={{ color: s.c, opacity: 0.6 }}
                  />
                </span>
                <span
                  className="font-display text-[7px] font-semibold text-[#475569] text-center leading-tight whitespace-nowrap"
                  style={{ letterSpacing: "0.02em" }}
                >
                  {s.l}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- small inline tab icons ---------- */
function TabIcon({ kind }) {
  const cn = "w-2.5 h-2.5 shrink-0";
  if (kind === "gmail")
    return (
      <svg viewBox="0 0 24 24" className={cn} aria-hidden>
        <path
          d="M3 6 L12 13 L21 6 V18 H3 Z"
          fill="none"
          stroke="#EA4335"
          strokeWidth="2.5"
        />
      </svg>
    );
  if (kind === "doc")
    return (
      <svg viewBox="0 0 24 24" className={cn} aria-hidden>
        <rect x="5" y="3" width="14" height="18" rx="2" fill="#2563EB" />
        <rect x="8" y="8" width="8" height="1.5" fill="white" />
        <rect x="8" y="12" width="8" height="1.5" fill="white" />
        <rect x="8" y="16" width="5" height="1.5" fill="white" />
      </svg>
    );
  if (kind === "note")
    return (
      <svg viewBox="0 0 24 24" className={cn} aria-hidden>
        <rect x="4" y="4" width="16" height="16" rx="2" fill="#FACC15" />
      </svg>
    );
  if (kind === "yt")
    return (
      <svg viewBox="0 0 24 24" className={cn} aria-hidden>
        <rect x="2" y="6" width="20" height="12" rx="3" fill="#EF4444" />
        <path d="M10 9 L16 12 L10 15 Z" fill="white" />
      </svg>
    );
  if (kind === "reddit")
    return (
      <svg viewBox="0 0 24 24" className={cn} aria-hidden>
        <circle cx="12" cy="12" r="10" fill="#FF4500" />
      </svg>
    );
  return null;
}

function CursorIcon() {
  return (
    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" aria-hidden>
      <path
        d="M2 1 L2 13 L5.5 10 L8 15 L10 14 L7.5 9 L12 9 Z"
        fill="#0F172A"
        stroke="white"
        strokeWidth="0.5"
      />
    </svg>
  );
}
